import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiCallWithRetry, client } from '@shared/api/client';

// LOCAL-ONLY safety net for a broken GET /user/preferences on the shared
// backend: without preferences every table page renders an error state
// and nothing else can be worked on. The real request ALWAYS goes out
// first; the saved copy is used only after it has failed, and only when
// the dev server was started with REACT_APP_LOCAL_PREFERENCES_FALLBACK=true
// on the command line. The flag lives in no .env file, so Azure builds
// for dev and prod never see it and always work with live data.
const useLocalPreferencesFallback = () =>
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_LOCAL_PREFERENCES_FALLBACK === 'true';

const loadLocalPreferencesFallback = async reason => {
  const { default: fixture } = await import('@shared/config/devFixtures/userPreferences.json');
  // Loud on purpose: nobody should mistake this for the server's data.
  // eslint-disable-next-line no-console
  console.warn('[preferences] GET /user/preferences failed, using the LOCAL fixture:', reason);
  return { ok: true, data: fixture, localFallback: true };
};

export const fetchTableSettings = createAsyncThunk(
  'table/fetchSettings',
  async (_, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.get('/user/preferences');
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);

      if (response.ok) {
        return { ok: true, data: response.data };
      }

      if (useLocalPreferencesFallback()) {
        return loadLocalPreferencesFallback(`HTTP ${response.status}`);
      }

      return rejectWithValue({
        error: response.data?.message || 'Failed to load table settings',
        status: response.status,
      });
    } catch (error) {
      if (useLocalPreferencesFallback()) {
        return loadLocalPreferencesFallback(error);
      }

      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
      });
    }
  }
);

export const sendNewTableSettings = createAsyncThunk(
  'table/pastSettings',
  async ({ path, patch }, { rejectWithValue }) => {
    if (!patch.length) return;

    const apiCall = async () => {
      return await client.patch('/user/preferences/' + path, patch);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);

      if (response.ok) {
        return { ok: true, data: response.data };
      }

      return rejectWithValue({
        error: response.data?.message || 'Failed to save table settings',
        status: response.status,
      });
    } catch (error) {
      return rejectWithValue({
        error: typeof error === 'string' ? error : 'Network error',
        status: null,
      });
    }
  }
);
